import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../../api/api";
import Modal from "../../components/Modal";
import Toast from "../../components/Toast";
import "./Gallery.css";

const IMG_BASE = "https://cmsapi.mks.org.au/uploads/";

function GalleryList() {
    const [list, setList] = useState([]);
    const [filteredList, setFilteredList] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({
        title: "",
        eventDate: new Date().toISOString().split('T')[0],
        description: "",
        coverImage: null
    });
    const [editingItem, setEditingItem] = useState(null);
    const [toast, setToast] = useState(null);

    const fetchList = () => {
        setLoading(true);
        console.log("Fetching gallery list from /gallery...");
        API.get("/gallery")
            .then(res => {
                console.log("Gallery list received:", res.data);
                setList(res.data);
                setFilteredList(res.data);
            })
            .catch(err => {
                console.error("Error fetching gallery list:", err);
                console.error("Error details:", err.response?.data || err.message);
                setList([]);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchList();
    }, []);

    useEffect(() => {
        const results = list.filter(x =>
            x.Title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            x.EventDate?.toString().includes(searchTerm)
        );
        setFilteredList(results);
    }, [searchTerm, list]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = new FormData();
            if (formData.coverImage) data.append("coverImage", formData.coverImage);

            const params = new URLSearchParams({
                title: formData.title,
                eventDate: formData.eventDate,
                description: formData.description
            }).toString();

            if (editingItem) {
                console.log("Updating Gallery Event:", editingItem.GalleryEventId);
                await API.put(`/gallery/${editingItem.GalleryEventId}?${params}`, data, {
                    headers: { "Content-Type": "multipart/form-data" }
                });
                showToast("Gallery event updated successfully");
            } else {
                console.log("Submitting Gallery Payload:", { ...formData });
                await API.post(`/gallery?${params}`, data, {
                    headers: { "Content-Type": "multipart/form-data" }
                });
                showToast("Gallery event created successfully");
            }

            setIsModalOpen(false);
            setEditingItem(null);
            fetchList();
            setFormData({ title: "", eventDate: new Date().toISOString().split('T')[0], description: "", coverImage: null });
        } catch (err) {
            showToast(`Error ${editingItem ? "updating" : "creating"} gallery event`, "error");
        }
    };

    const handleEdit = (e, item) => {
        e.preventDefault();
        e.stopPropagation();
        setEditingItem(item);
        setFormData({
            title: item.Title,
            eventDate: item.EventDate ? item.EventDate.split('T')[0] : "",
            description: item.Description || "",
            coverImage: null
        });
        setIsModalOpen(true);
    };

    const handleDelete = async (e, id) => {
        e.preventDefault();
        e.stopPropagation();
        if (window.confirm("Are you sure you want to delete this gallery event? This action cannot be undone.")) {
            try {
                await API.delete(`/gallery/${id}`);
                showToast("Gallery event deleted successfully");
                fetchList();
            } catch (err) {
                showToast("Error deleting gallery event", "error");
            }
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);
        setFormData({ title: "", eventDate: new Date().toISOString().split('T')[0], description: "", coverImage: null });
    };

    const showToast = (message, type = "success") => {
        setToast({ message, type });
    };

    const formatEventDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;

        const day = date.getDate();
        const month = date.toLocaleString('en-US', { month: 'long' });
        const year = date.getFullYear();

        const getOrdinal = (n) => {
            const s = ["th", "st", "nd", "rd"];
            const v = n % 100;
            return (s[(v - 20) % 10] || s[v] || s[0]);
        };

        return `${day}${getOrdinal(day)} ${month}, ${year}`;
    };

    return (
        <div className="admin-card animate-fade-in">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "35px", flexWrap: "wrap", gap: "20px" }}>
                <div>
                    <h2 style={{ margin: 0, color: "var(--text-dark)", fontSize: "1.75rem", fontWeight: "800" }}>Gallery Events</h2>
                    <p style={{ margin: "5px 0 0 0", color: "var(--text-muted)", fontSize: "0.9rem" }}>Manage community photo albums and event galleries</p>
                </div>
                <div style={{ display: "flex", gap: "15px", alignItems: "center", flex: 1, justifyContent: "flex-end" }}>
                    <div className="search-container">
                        <span className="search-icon">🔍</span>
                        <input
                            className="form-control"
                            placeholder="Search by title or date..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button className="btn btn-primary" onClick={() => { setEditingItem(null); setIsModalOpen(true); }} style={{ height: "45px" }}>
                        + Add Gallery
                    </button>
                </div>
            </div>

            {loading ? (
                <div style={{ textAlign: "center", padding: "100px" }}>
                    <div className="spinner"></div>
                    <p style={{ marginTop: "15px", color: "var(--text-muted)" }}>Loading galleries...</p>
                </div>
            ) : (
                <div className="gallery-grid">
                    {filteredList.map(x => {
                        let coverPath = x.CoverImage || "";
                        // If it's a relative path and doesn't explicitly start with 'gallery/', add it
                        if (coverPath && !coverPath.startsWith('http') && !coverPath.startsWith('gallery/')) {
                            coverPath = "gallery/" + coverPath;
                        }
                        const imgUrl = coverPath ? (coverPath.startsWith('http') ? coverPath : IMG_BASE + coverPath) : "https://via.placeholder.com/400x300?text=No+Cover";
                        return (
                            <Link key={x.GalleryEventId} to={`/gallery/${x.GalleryEventId}`} className="gallery-card">
                                <div className="gallery-cover-wrapper">
                                    <img src={imgUrl} className="gallery-cover-img" alt={x.Title} />
                                    <span className="year-badge">{formatEventDate(x.EventDate)}</span>
                                </div>
                                <div className="gallery-card-content">
                                    <h3 className="gallery-card-title">{x.Title}</h3>
                                    {x.Description && (
                                        <p className="gallery-card-description" style={{ fontSize: "0.85rem", color: "var(--text-muted)", margin: "0 0 15px 0" }}>
                                            {x.Description}
                                        </p>
                                    )}
                                    <div className="gallery-card-footer">
                                        <div className="gallery-card-action">
                                            📁 Manage Photos
                                        </div>
                                        <div className="gallery-card-options">
                                            <button className="option-btn edit" onClick={(e) => handleEdit(e, x)} title="Edit Event">✏️</button>
                                            <button className="option-btn delete" onClick={(e) => handleDelete(e, x.GalleryEventId)} title="Delete Event">🗑️</button>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}

            <Modal isOpen={isModalOpen} onClose={closeModal} title={editingItem ? "Edit Gallery Event" : "Create Gallery Event"}>
                <form onSubmit={handleSubmit} className="premium-form">
                    <div className="form-group">
                        <label>Title (Event-wise categorization)</label>
                        <input className="form-control" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} required placeholder="e.g., Diwali 2026" />
                    </div>
                    <div className="form-group">
                        <label>Description (Optional)</label>
                        <textarea className="form-control" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} placeholder="Write something about this event..." style={{ height: "100px", resize: "vertical" }}></textarea>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                        <div className="form-group">
                            <label>Event Date</label>
                            <input type="date" className="form-control" value={formData.eventDate} onChange={e => setFormData({ ...formData, eventDate: e.target.value })} required />
                        </div>
                        <div className="form-group">
                            <label>Cover Image {editingItem && "(Leave empty to keep current)"}</label>
                            <input type="file" className="form-control" onChange={e => setFormData({ ...formData, coverImage: e.target.files[0] })} required={!editingItem} />
                        </div>
                    </div>
                    <div style={{ marginTop: "30px", display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                        <button type="button" className="btn btn-outline" onClick={closeModal}>Cancel</button>
                        <button type="submit" className="btn btn-primary">{editingItem ? "Update Gallery Event" : "Create Gallery Event"}</button>
                    </div>
                </form>
            </Modal>

            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        </div>
    );
}

export default GalleryList;